import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import faqIllustration from '../../assets/gg__illustration--co-working-space-3.svg';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'Is there a cost to using Grant Guardian?',
    answer:
      'Grant Guardian is 100% free to use for U.S.-based verified philanthropic institutions!',
  },
  {
    question: 'Who Built Grant Guardian?',
    answer:
      'Grant Guardian was built by the Products and Services (P&S) team at the Patrick J. McGovern Foundation, a group of data scientists and engineers dedicated to building data and AI products for the public good. You can learn more about P&S on our website.',
  },
  {
    question: 'Is our data secure and private?',
    answer:
      "Grant Guardian was developed with robust data security and privacy best practices.\n\nGrant Guardian utilizes Amazon's AWS Cognito, an industry-standard identity and access management service. Sign-in is passwordless — users enter their email address and verify a one-time code sent to their inbox, so there are no passwords to leak or reuse.\n\nAll data uploaded to Grant Guardian is private to the foundation that uploaded it, and no other foundation has access to it. User file uploads and data, including analyses, are encrypted both at rest and in transit. Access to the Grant Guardian database is strictly controlled and limited to a select number of engineers with specific permissions, typically to perform queries if a user encounters a bug. If a foundation chooses to no longer use Grant Guardian, all data uploaded by the foundation can be bulk downloaded, and will otherwise be deleted/removed when the account is deactivated.",
  },
  {
    question: 'What AI model was used?',
    answer:
      'Grant Guardian uses Claude 3.7 and 4.0, a Large Language Model (LLM) built by Anthropic, and prompt engineering techniques developed by PJMF. When prompted to query the financial documents, the LLM extracts the relevant information and uses it to calculate a set of indicators that are defined by the grantmaker.',
  },
  {
    question:
      "I'm a nonprofit. What specifically is Grant Guardian looking for in my financial data?",
    answer:
      "Grant Guardian supports grantmakers in their already established practices of incorporating a review of a nonprofit's financial position into a broader funding decision. Below are several financial indicators that grantmakers may choose to use within Grant Guardian, along with how they are calculated and why they are relevant to analyses of nonprofits' financial position:\n\nCurrent Ratio - Measures an organization's ability to meet short-term obligations such as payroll, debt obligations, and other expenses when they become due.\nFormula: Current Assets ÷ Current Liabilities. Source: Statement of Financial Position. \n\nCash Reserve Ratio - Assesses how many months of liquid, unrestricted funds the organization has on hand to manage fluctuations in cash flow or respond to unplanned opportunities or crises without accessing lines of credit.\nFormula: (Unrestricted Cash + Liquid Investments) ÷ Average Monthly Expenses. Source: Statement of Financial Position (for assets) and Statement of Activity (for expenses).\n\nDonor-restricted Net Assets to Total Net Assets Ratio - Indicates the proportion of net assets that are donor-restricted versus available for flexible use at the nonprofit's discretion.\nFormula: Donor-Restricted Net Assets ÷ (Total Assets – Total Liabilities). Source: Statement of Financial Position.\n\nAll financial ratios and indicators on Grant Guardian are fully customizable, allowing funders to set review thresholds based on the specific characteristics of the nonprofits they support. For example, a funder working primarily with larger, more established organizations may choose to set a Current Ratio review threshold of 1.0 (where anything less than 1.0 is highlighted for review), while a funder focused on early-stage or smaller nonprofits may set the threshold at 0.5 to account for different liquidity norms and less predictable income.",
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = useState<string | false>('panel0');

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <Box id="faqs" sx={{ py: 10, backgroundColor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} sx={{ alignItems: 'center' }}>
          {/* Image */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Box
                component="img"
                src={faqIllustration}
                alt="FAQ"
                sx={{ width: '100%', height: 'auto' }}
              />
            </motion.div>
          </Grid>

          {/* FAQ Accordion */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  fontWeight: 700,
                  mb: 4,
                }}
              >
                Questions & Answers
              </Typography>

              {faqs.map((faq, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === `panel${index}`}
                  onChange={handleChange(`panel${index}`)}
                  sx={{
                    mb: 2,
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
